import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useVehiclePartnerActions } from '@/hooks/use-vehicle-partner-actions';
import { findPartnerLinkedSource } from './funding-sources-coordination/partner-funding-utils';
import { MirroredSourceStatus } from './funding-sources-coordination/MirroredSourceStatus';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile,
} from '@design-system';
import { SessionType, isNullOrUndefined, formatCurrency as sharedFormatCurrency } from '@shared';
import { Info } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { CurrencyInput, PercentInput } from '..';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '../model';
import { FormErrorAlert } from './FormErrorAlert';
import {
  PartnerAllocationSection,
  type AllocationOption,
} from './funding-sources-coordination/partner-allocation-section';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { Input } from './ui/input';

interface SelfFundingDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingSourceId?: string;
}

type SelfFundingKey = keyof SelfFundingSource;

const formatCurrency = (value: number) => sharedFormatCurrency(value, 0);

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-red-400">{message}</p>;
}

function FieldLabel({ label, tooltip }: { label: string; tooltip: string | ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="text-muted-foreground size-3.5 cursor-help" />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs"
        >
          <p className="text-sm">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function SelfFundingDetailsModal({
  open,
  setOpen,
  fundingSourceId,
}: SelfFundingDetailsModalProps) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    partnerFirstName,
    partnerClientId,
    saveWithPartnerSync,
    handleUnlink,
  } = useVehiclePartnerActions();
  const { flags } = useFeatureFlags();

  const {
    clientId,
    multipleFundingSources,
    intakeSurvey: { clientHasStartedLtc, clientBirthDate },
    appliedInferenceSet: { ltcAtYear, yearsTillLtc, ltcAtAge },
    allPhaseCosts,
  } = client;

  const selfFunding =
    fundingSourceId && multipleFundingSources.selfFunding[fundingSourceId]
      ? multipleFundingSources.selfFunding[fundingSourceId]
      : ({
          hasSelfFunding: false,
          existingAssetsValue: null,
          annualRateOfReturn: null,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: null,
          gainsTaxRate: null,
          monthlyIncome: null,
          contributionMonthCount: 0,
        } as SelfFundingSource);

  const requiredKeys: SelfFundingKey[] = clientHasStartedLtc
    ? ['existingAssetsValue']
    : ['existingAssetsValue', 'annualRateOfReturn', 'gainsTaxRate'];

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isExistingMirror = selfFunding?.partnerAction === 'mirrored';
  const existingSplitRatio = selfFunding?.splitRatio ?? null;

  const partnerLinkedSource =
    isExistingMirror && selfFunding?.fundingSourceGroupId
      ? findPartnerLinkedSource(
          client.mutableClientPartner?.multipleFundingSources?.selfFunding,
          selfFunding.fundingSourceGroupId,
        )?.[1]
      : undefined;

  const hasSplitWithPartner = isExistingMirror && existingSplitRatio != null && partnerLinkedSource;
  const initialTotalAssets = hasSplitWithPartner
    ? (selfFunding.existingAssetsValue ?? 0) + (partnerLinkedSource.existingAssetsValue ?? 0)
    : selfFunding.existingAssetsValue;

  const initialSelfFundingValue: SelfFundingSource = {
    ...selfFunding,
    existingAssetsValue: initialTotalAssets,
    ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
  };

  const [selfFundingState, setSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [formErrors, setFormErrors] = useState({} as Record<SelfFundingKey, string>);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [initialSelfFundingState, setInitialSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [showWarning, setShowWarning] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [mirrorChecked, setMirrorChecked] = useState(false);
  const [allocationOption, setAllocationOption] = useState<AllocationOption>('mirror');
  const [customClientPercent, setCustomClientPercent] = useState(50);

  const showPartnerSection =
    Boolean(partnerFirstName) && Boolean(partnerClientId) && flags.selfFundingPartnerSync === true;
  const clientName = client.clientFirstName ?? 'Client';
  const totalAssets = selfFundingState.existingAssetsValue ?? 0;

  const handleChangeSelfFunding = (selfFundingKey: SelfFundingKey) => (value: number) => {
    setSelfFundingState(prev => ({ ...prev, [selfFundingKey]: value }));
  };

  function handleChangeSelfFundingName(value: string) {
    setSelfFundingState(prev => ({ ...prev, selfFundingName: value }));
  }

  function validateForm() {
    const errors = {} as Record<SelfFundingKey, string>;
    requiredKeys.forEach(key => {
      if (isNullOrUndefined(selfFundingState[key])) {
        errors[key] = 'Required Field';
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleClickSubmit() {
    setFormHasBeenSubmitted(true);
    if (!validateForm()) return;

    const updatedSelfFunding: SelfFundingSource = {
      ...selfFundingState,
      hasSelfFunding: true,
      selfFundingName: selfFundingState.selfFundingName || 'Self-funding',
    };

    const shouldSyncPartner =
      (isExistingMirror && fundingSourceId) || (mirrorChecked && partnerClientId);

    if (shouldSyncPartner) {
      saveWithPartnerSync({
        fundingSourceId,
        source: updatedSelfFunding,
        isExistingMirror,
        existingSplitRatio,
        allocationOption,
        clientPercent: customClientPercent,
      });
    } else {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });
    setOpen(false);
  }

  const formIsValid = Object.keys(formErrors).length === 0;
  const hasRequiredFieldsErrors = Object.keys(formErrors).some(key =>
    requiredKeys.includes(key as SelfFundingKey),
  );

  function resetForm() {
    setSelfFundingState(selfFunding ?? null);
    setFormErrors({} as Record<SelfFundingKey, string>);
    setMirrorChecked(false);
    setAllocationOption('mirror');
    setCustomClientPercent(50);
  }

  function onClose() {
    if (JSON.stringify(selfFundingState) !== JSON.stringify(initialSelfFundingState)) {
      setShowWarning(true);
    } else {
      setOpen(false);
      resetForm();
    }
  }

  function handleConfirmClose() {
    setShowWarning(false);
    setOpen(false);
    resetForm();
  }

  function handleCancelClose() {
    setShowWarning(false);
  }

  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [
    selfFundingState.annualRateOfReturn,
    selfFundingState.existingAssetsValue,
    selfFundingState.gainsTaxRate,
  ]);

  useEffect(() => {
    if (open) {
      const reconstructedAssets = hasSplitWithPartner
        ? (selfFunding.existingAssetsValue ?? 0) + (partnerLinkedSource.existingAssetsValue ?? 0)
        : selfFunding.existingAssetsValue;

      let openingSelfFundingState: SelfFundingSource = selfFunding
        ? { ...selfFunding, hasSelfFunding: true, existingAssetsValue: reconstructedAssets }
        : ({ hasSelfFunding: false } as SelfFundingSource);

      if (isAdvisorSession) {
        openingSelfFundingState = {
          ...openingSelfFundingState,
          addedByAdvisorId: advisorId,
        };
      }

      setSelfFundingState(openingSelfFundingState);
      setInitialSelfFundingState(openingSelfFundingState);
      setFormHasBeenSubmitted(false);
      setMirrorChecked(false);
      setAllocationOption('mirror');
      setCustomClientPercent(selfFunding?.splitRatio ?? 50);
    }
  }, [open]);

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingState, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded: allPhaseCosts?.allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const showPreview =
    flags.selfFundingGrowthPreview === true &&
    !clientHasStartedLtc &&
    projectedValue != null &&
    selfFundingState != null;

  const formContent = (
    <div
      className="space-y-5"
      data-intercom-target="Self Funding Fields"
    >
      {showPreview && (
        <Alert
          variant="info"
          className="bg-[#F8F6FD]"
        >
          <Info className="size-4" />
          <AlertDescription>
            <span className="whitespace-nowrap">
              {ageAtLtc != null
                ? `Projected value by age ${ageAtLtc}: `
                : `Projected value by ${ltcAtYear}: `}
              <span className="font-semibold">{formatCompactCurrency(projectedValue)}</span>
            </span>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <FieldLabel
          label="Name (optional)"
          tooltip="Optional name to identify this self-funding source"
        />
        <Input
          type="text"
          placeholder="Self-funding"
          value={selfFundingState.selfFundingName ?? ''}
          onChange={e => handleChangeSelfFundingName(e.target.value)}
          className="h-9 border-neutral-300 text-sm"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel
            label="Available Assets"
            tooltip="Present-day value of assets that can be reserved for future long-term care expenses"
          />
          <CurrencyInput
            label=""
            placeholder="Enter dollars"
            value={selfFundingState.existingAssetsValue}
            onChange={handleChangeSelfFunding('existingAssetsValue')}
            onPreview={handleChangeSelfFunding('existingAssetsValue')}
          />
          <ErrorMessage message={formErrors.existingAssetsValue} />
        </div>

        {!clientHasStartedLtc && (
          <div className="space-y-1.5">
            <FieldLabel
              label="Annual Rate of Return"
              tooltip="The expected annual rate of return applied to both existing assets and monthly contributions"
            />
            <PercentInput
              label=""
              placeholder="Enter percent"
              value={selfFundingState.annualRateOfReturn}
              onChange={handleChangeSelfFunding('annualRateOfReturn')}
              onPreview={handleChangeSelfFunding('annualRateOfReturn')}
            />
            <ErrorMessage message={formErrors.annualRateOfReturn} />
          </div>
        )}
      </div>

      {!clientHasStartedLtc && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel
              label="Gains Tax Rate"
              tooltip={
                <>
                  The tax rate on investment profits. For more info, view the{' '}
                  <a
                    href="https://www.irs.gov/taxtopics/tc409"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    IRS article
                  </a>
                  .
                </>
              }
            />
            <PercentInput
              label=""
              placeholder="Enter percent"
              value={selfFundingState.gainsTaxRate}
              onChange={handleChangeSelfFunding('gainsTaxRate')}
              onPreview={handleChangeSelfFunding('gainsTaxRate')}
            />
            <ErrorMessage message={formErrors.gainsTaxRate} />
          </div>
        </div>
      )}

      {!clientHasStartedLtc && (
        <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel
              label="Monthly Contribution (optional)"
              tooltip="Money added each month to assets or investments, aimed at increasing the total value available to fund long-term care expenses"
            />
            <CurrencyInput
              label=""
              placeholder="Enter dollars"
              value={selfFundingState.monthlyContributionAmount}
              onChange={handleChangeSelfFunding('monthlyContributionAmount')}
              onPreview={handleChangeSelfFunding('monthlyContributionAmount')}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel
              label="Monthly Income (optional)"
              tooltip="Future expected income during LTC years (social security, pension, rental income) that can cover LTC costs. Enter the monthly equivalent."
            />
            <CurrencyInput
              label=""
              placeholder="Enter dollars"
              value={selfFundingState.monthlyIncome}
              onChange={handleChangeSelfFunding('monthlyIncome')}
              onPreview={handleChangeSelfFunding('monthlyIncome')}
            />
          </div>
        </div>
      )}

      {clientHasStartedLtc && (
        <div className="space-y-1.5">
          <FieldLabel
            label="Monthly Income"
            tooltip="Future expected income during LTC years (social security, pension, rental income) that can cover LTC costs. Enter the monthly equivalent."
          />
          <CurrencyInput
            label=""
            placeholder="Enter dollars"
            value={selfFundingState.monthlyIncome}
            onChange={handleChangeSelfFunding('monthlyIncome')}
            onPreview={handleChangeSelfFunding('monthlyIncome')}
          />
        </div>
      )}

      {showPartnerSection && (
        <div className="space-y-3 border-t pt-4">
          {isExistingMirror ? (
            <MirroredSourceStatus
              clientName={clientName}
              partnerName={partnerFirstName ?? ''}
              splitRatio={existingSplitRatio}
              totalAssets={totalAssets}
              onUnlink={() => setShowUnlinkConfirm(true)}
            />
          ) : (
            <PartnerAllocationSection
              enabled={mirrorChecked}
              onEnabledChange={checked => {
                setMirrorChecked(checked);
                if (!checked) {
                  setAllocationOption('mirror');
                  setCustomClientPercent(50);
                }
              }}
              value={totalAssets}
              clientName={clientName}
              partnerName={partnerFirstName ?? ''}
              selectedOption={allocationOption}
              onOptionChange={setAllocationOption}
              customClientPercent={customClientPercent}
              onCustomClientPercentChange={setCustomClientPercent}
              className="border-t-0 px-0 pb-0 pt-0"
            />
          )}
        </div>
      )}

      {!formIsValid && (
        <FormErrorAlert mainErrorMessage="There is an error with your submission">
          {hasRequiredFieldsErrors && (
            <li>
              <span className="font-bold">Required field:</span> Please fill out all required
              fields.
            </li>
          )}
        </FormErrorAlert>
      )}
    </div>
  );

  const submitButton = (
    <Button
      className="w-full"
      onClick={handleClickSubmit}
    >
      Submit
    </Button>
  );

  return (
    <>
      <UnsavedChangesDialog
        showWarning={showWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />
      <UnsavedChangesDialog
        showWarning={showUnlinkConfirm}
        handleCancelClose={() => setShowUnlinkConfirm(false)}
        handleConfirmClose={() => {
          setShowUnlinkConfirm(false);
          if (fundingSourceId) {
            handleUnlink(fundingSourceId, selfFunding as SelfFundingSource);
            setOpen(false);
          }
        }}
        dialogTitle={`Unlink from ${partnerFirstName}'s plan?`}
        dialogDescription={`This will disconnect the two accounts. ${clientName}'s and ${partnerFirstName}'s accounts will become independent and future changes to one will no longer update the other.`}
        primaryButtonText="Unlink"
        secondaryButtonText="Cancel"
        destructive={false}
      />
      {isMobile ? (
        <Drawer
          open={open}
          onOpenChange={onClose}
        >
          <DrawerContent className="max-h-[85vh] overflow-y-auto px-4">
            <DrawerHeader className="text-left">
              <DrawerTitle>Self-Funding Details</DrawerTitle>
              <DrawerDescription>Configure your self-funding source</DrawerDescription>
            </DrawerHeader>
            <div className="py-4">{formContent}</div>
            <DrawerFooter className="px-0">{submitButton}</DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onOpenChange={onClose}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Self-Funding Details</DialogTitle>
              <DialogDescription>Configure your self-funding source</DialogDescription>
            </DialogHeader>
            <div className="py-2">{formContent}</div>
            {submitButton}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
